import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	ChecklistTemplateCreateBody,
	ChecklistTemplateItemCreateBody,
	ChecklistTemplateItemUpdateBody,
	ChecklistTemplateUpdateBody,
} from '@/domain/workflow-admin.types'
import { workflowAdminApi } from '@/services/workflow.service'

/** React Query hooks for the checklist template library (`checklist_template` + its items). */

const ROOT = 'checklist-template-admin'

export const checklistTemplateAdminKeys = {
	all: () => [ROOT] as const,
	template: (templateId: string | null) => [ROOT, templateId] as const,
}

export const useChecklistTemplates = () =>
	useQuery({ queryKey: checklistTemplateAdminKeys.all(), queryFn: workflowAdminApi.listChecklistTemplates })

export const useChecklistTemplate = (templateId: string | null) =>
	useQuery({
		queryKey: checklistTemplateAdminKeys.template(templateId),
		queryFn: () => workflowAdminApi.getChecklistTemplate(templateId as string),
		enabled: !!templateId,
	})

export const useCreateChecklistTemplate = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: ChecklistTemplateCreateBody) => workflowAdminApi.createChecklistTemplate(body),
		onSuccess: () => void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.all() }),
	})
}

export const useUpdateChecklistTemplate = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ templateId, body }: { templateId: string; body: ChecklistTemplateUpdateBody }) =>
			workflowAdminApi.updateChecklistTemplate(templateId, body),
		onSuccess: (_data, { templateId }) => {
			void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.all() })
			void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.template(templateId) })
		},
	})
}

export const useDeleteChecklistTemplate = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (templateId: string) => workflowAdminApi.deleteChecklistTemplate(templateId),
		onSuccess: () => void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.all() }),
	})
}

export const useCreateChecklistTemplateItem = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: (body: ChecklistTemplateItemCreateBody) => workflowAdminApi.createChecklistTemplateItem(body),
		onSuccess: (_data, body) =>
			void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.template(body.templateId) }),
	})
}

export const useUpdateChecklistTemplateItem = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({
			templateId,
			itemId,
			body,
		}: {
			templateId: string
			itemId: string
			body: ChecklistTemplateItemUpdateBody
		}) => workflowAdminApi.updateChecklistTemplateItem(templateId, itemId, body),
		onSuccess: (_data, { templateId }) =>
			void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.template(templateId) }),
	})
}

export const useDeleteChecklistTemplateItem = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: ({ templateId, itemId }: { templateId: string; itemId: string }) =>
			workflowAdminApi.deleteChecklistTemplateItem(templateId, itemId),
		onSuccess: (_data, { templateId }) =>
			void qc.invalidateQueries({ queryKey: checklistTemplateAdminKeys.template(templateId) }),
	})
}
